import { useId } from 'react';

import { Button } from '@/components/Button/Button';

import type {
  EditableGradingRubric,
  EditableRubricUnit,
} from '../editorTypes';
import {
  createDraftRubricUnit,
  getRubricTotalScore,
  normalizeGradingRubric,
} from '../questionDialogUtils';
import { RubricUnitRow } from './RubricUnitRow';
import styles from './RubricEditor.module.css';

interface RubricEditorProps {
  disabled?: boolean;
  onChange: (rubric: EditableGradingRubric) => void;
  rubric: EditableGradingRubric | undefined;
  validationError?: string;
}

function formatPoints(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}

function buildRubric(units: EditableRubricUnit[]): EditableGradingRubric {
  return {
    rubricUnits: units,
    totalScore: getRubricTotalScore({ rubricUnits: units, totalScore: 0 }),
  };
}

export function RubricEditor({
  disabled = false,
  onChange,
  rubric,
  validationError,
}: RubricEditorProps) {
  const headingId = useId();
  const normalizedRubric = normalizeGradingRubric(rubric);
  const totalScore = getRubricTotalScore(normalizedRubric);

  function updateUnit(index: number, updates: Partial<EditableRubricUnit>) {
    onChange(
      buildRubric(
        normalizedRubric.rubricUnits.map((unit, currentIndex) =>
          currentIndex === index
            ? {
                ...unit,
                ...updates,
              }
            : unit,
        ),
      ),
    );
  }

  function removeUnit(index: number) {
    if (normalizedRubric.rubricUnits.length <= 1) {
      return;
    }

    onChange(
      buildRubric(
        normalizedRubric.rubricUnits.filter(
          (_unit, currentIndex) => currentIndex !== index,
        ),
      ),
    );
  }

  function addUnit() {
    onChange(
      buildRubric([
        ...normalizedRubric.rubricUnits,
        createDraftRubricUnit(normalizedRubric.rubricUnits.length),
      ]),
    );
  }

  return (
    <section className={styles.root} aria-labelledby={headingId}>
      <div className={styles.header}>
        <div>
          <h4 className={styles.title} id={headingId}>
            Grading Rubric
          </h4>
          <p className={styles.help}>
            KAI uses these rubric units to evaluate the uploaded file.
          </p>
        </div>
        <p className={styles.total}>
          Total: {formatPoints(totalScore)} {totalScore === 1 ? 'pt' : 'pts'}
        </p>
      </div>

      {validationError ? (
        <p className={styles.error} role="alert">
          {validationError}
        </p>
      ) : null}

      <ol className={styles.list}>
        {normalizedRubric.rubricUnits.map((unit, index) => (
          <RubricUnitRow
            canRemove={normalizedRubric.rubricUnits.length > 1}
            disabled={disabled}
            index={index}
            key={unit.id}
            onChange={(updates) => updateUnit(index, updates)}
            onRemove={() => removeUnit(index)}
            unit={unit}
          />
        ))}
      </ol>

      <Button disabled={disabled} onClick={addUnit} size="small" variant="secondary">
        Add Rubric Unit
      </Button>
    </section>
  );
}
